import { QRCodeSVG } from 'qrcode.react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';

const Demo = () => {
  const [zoomedPage, setZoomedPage] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const PageContent = ({ side }: { side: 'left' | 'right' }) => {
    const isZoomed = zoomedPage === side;
    const baseClasses = "bg-white shadow-lg border-gray-200 transition-all duration-300";
    const sizeClasses = isZoomed 
      ? "fixed top-0 left-1/2 -translate-x-1/2 z-50" 
      : `w-[300px] h-[400px] ${side === 'left' ? 'rounded-l-lg rotate-y-1 border-r' : 'rounded-r-lg rotate-y-[-1deg] border-l'}`;

    const pageContent = (
      <div className={`w-[300px] h-[400px] ${isZoomed ? 'transform scale-[2.0] origin-top bg-white rounded-lg shadow-2xl' : ''} relative`}>
        <button
          onClick={() => setZoomedPage(isZoomed ? null : side)}
          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors z-50"
        >
          {isZoomed ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
        <div className="p-6 h-full">
          <div className="text-[6px] leading-tight space-y-2">
            {side === 'left' ? (
              <>
                <h2 className="text-[8px] font-bold mb-2">Individueller Kalorienbedarf</h2>
                <p>
                  Um deinen Körper optimal mit Nährstoffen zu versorgen, ist es entscheidend, den individuellen Bedarf zu kennen. Jeder Mensch ist einzigartig, und pauschale Ernährungsempfehlungen führen oft nicht zum Ziel. Faktoren wie Alter, Geschlecht, Gewicht, Körpergröße und vor allem das Aktivitätsniveau spielen eine wichtige Rolle bei der Berechnung des persönlichen Kalorienbedarfs.
                </p>
                <img
                  src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                  alt="Fitness Training"
                  className="w-full h-24 object-cover rounded-lg my-2"
                />
                <p className="text-center text-[5px] text-gray-600 mb-2">Individuelles Training für optimale Ergebnisse</p>
                <h3 className="text-[7px] font-semibold">Grundumsatz (BMR)</h3>
                <p>
                  Der Grundumsatz, auch Basalstoffwechsel oder BMR (Basal Metabolic Rate) genannt, beschreibt die Energiemenge, die dein Körper in völliger Ruhe benötigt, um lebenswichtige Funktionen aufrechtzuerhalten. Dazu gehören Atmung, Herzschlag, Körpertemperatur und Stoffwechsel. Der BMR macht etwa 60-75% deines täglichen Gesamtenergiebedarfs aus.
                </p>
                <p>
                  Die Höhe des Grundumsatzes wird von verschiedenen Faktoren beeinflusst. Eine besonders wichtige Rolle spielt dabei die Körperzusammensetzung, insbesondere die Muskelmasse. Muskelgewebe ist stoffwechselaktiver als Fettgewebe und benötigt auch in Ruhe mehr Energie. Daher haben Menschen mit einem höheren Muskelanteil in der Regel auch einen höheren Grundumsatz.
                </p>
                <p>
                  Auch das Alter beeinflusst den Grundumsatz maßgeblich. Mit zunehmendem Alter sinkt der Grundumsatz typischerweise, was hauptsächlich auf den natürlichen Verlust von Muskelmasse und die Veränderung des Hormonhaushalts zurückzuführen ist. Männer haben aufgrund ihrer durchschnittlich höheren Muskelmasse meist einen höheren Grundumsatz als Frauen gleichen Alters und Gewichts.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-[7px] font-semibold">Leistungsumsatz (PAL)</h3>
                <p>
                  Der PAL-Wert (Physical Activity Level) beschreibt das Maß der körperlichen Aktivität und ist ein wichtiger Multiplikator bei der Berechnung des täglichen Energiebedarfs. Je nach Lebensstil und Aktivitätsniveau variiert dieser Wert zwischen 1,2 und 2,4:
                </p>
                <ul className="list-disc pl-3 space-y-1">
                  <li>1,2: Ausschließlich sitzende Tätigkeit</li>
                  <li>1,4-1,5: Sitzende Tätigkeit, wenig Aktivität</li>
                  <li>1,6-1,7: Sitzende Tätigkeit, zeitweise stehend/gehend</li>
                  <li>1,8-1,9: Überwiegend stehende/gehende Tätigkeit</li>
                  <li>2,0-2,4: Körperlich anstrengende Tätigkeit</li>
                </ul>
                <h3 className="text-[7px] font-semibold mt-2">Thermischer Effekt</h3>
                <p>
                  Der thermische Effekt der Nahrung (TEF) beschreibt die Energie, die der Körper für die Verdauung, Absorption und Verarbeitung der aufgenommenen Nahrung benötigt. Dieser macht etwa 10% des täglichen Energieverbrauchs aus.
                </p>
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <h4 className="text-[6px] font-semibold mb-1">Gesamtenergieumsatz Berechnung:</h4>
                  <p className="text-[5px]">Grundumsatz × PAL + TEF = Gesamtenergieumsatz</p>
                </div>
                <h3 className="text-[7px] font-semibold mt-2">Individuelle Anpassung</h3>
                <p>
                  Die berechneten Werte dienen als Orientierung und sollten individuell angepasst werden. Faktoren wie Krankheiten, Stress, Hormone und Umgebungstemperatur können den tatsächlichen Energiebedarf beeinflussen.
                </p>
                <div className="mt-2">
                  <QRCodeSVG
                    value="https://fitness-calculator.replit.app/"
                    size={40}
                    className="mx-auto"
                  />
                  <p className="text-center text-[5px] text-gray-600 mt-1">
                    Scanne für den Kalorienrechner
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );

    return (
      <div className={`${baseClasses} ${sizeClasses}`}>
        <div className="relative">
          {isZoomed && (
            <div className="fixed inset-0 z-40" onClick={() => setZoomedPage(null)} />
          )}
          {pageContent}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <Link
          to="/#demos"
          className="inline-flex items-center text-[#0055AA] hover:text-[#003366] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zu den Demos
        </Link>
        
        <div className="perspective-1000">
          <div className="flex justify-center gap-1">
            <PageContent side="left" />
            <PageContent side="right" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Demo;